import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageSquare, Send, Search, Loader2, UserX } from 'lucide-react';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function MessagesPage() {
  const { user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient?.user_id) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [selectedPatient]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/patients');
      setPatients(response.data);
    } catch (error) {
      toast.error('Error al cargar pacientes');
    }
  };

  const fetchMessages = async () => {
    if (!selectedPatient?.user_id || !user?.id) return;

    try {
      const response = await api.get(`/messages/${user.id}/${selectedPatient.user_id}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    setMessages([]);
    if (!patient.user_id) {
      // Patient doesn't have a user account - can't chat
      return;
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedPatient) return;

    if (!selectedPatient.user_id) {
      toast.error('Este paciente no tiene cuenta de usuario. Necesita registrarse para recibir mensajes.');
      return;
    }

    try {
      setSendingMessage(true);
      await api.post('/messages', {
        sender_id: user.id,
        receiver_id: selectedPatient.user_id,
        message: newMessage.trim()
      });

      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar el mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredPatients = patients.filter((patient) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      patient.first_name.toLowerCase().includes(searchLower) ||
      patient.last_name.toLowerCase().includes(searchLower) ||
      patient.dni.includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="messages-title">
          Mensajes
        </h1>
        <p className="text-muted-foreground mt-2">
          Comunicación con tus pacientes
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: 'calc(100vh - 250px)' }}>
        {/* Lista de conversaciones */}
        <Card className="p-4 overflow-y-auto" data-testid="conversations-list">
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Mis Pacientes
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar paciente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-patient-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            {filteredPatients.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {searchTerm ? 'No se encontraron pacientes' : 'No hay pacientes registrados'}
              </p>
            ) : (
              filteredPatients.map((patient) => (
                <div
                  key={patient.id}
                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                    selectedPatient?.id === patient.id
                      ? 'bg-primary/10 border-primary'
                      : 'hover:bg-muted border-transparent'
                  }`}
                  onClick={() => selectPatient(patient)}
                  data-testid={`patient-chat-${patient.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        {patient.first_name} {patient.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground">DNI: {patient.dni}</p>
                    </div>
                    {!patient.user_id && (
                      <span className="text-xs text-orange-500" title="Sin cuenta de usuario">
                        <UserX className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Área de chat */}
        <Card className="col-span-2 flex flex-col" data-testid="chat-area">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedPatient
                ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
                : 'Selecciona una conversación'}
            </h2>
            {selectedPatient && (
              <p className="text-xs text-muted-foreground">DNI: {selectedPatient.dni}</p>
            )}
          </div>

          <div className="flex-1 p-4 overflow-y-auto" data-testid="messages-container">
            {!selectedPatient ? (
              <div className="flex flex-col items-center justify-center h-full">
                <MessageSquare className="w-16 h-16 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center">
                  Selecciona un paciente para iniciar una conversación
                </p>
              </div>
            ) : !selectedPatient.user_id ? (
              <div className="flex flex-col items-center justify-center h-full">
                <UserX className="w-16 h-16 text-orange-400 mb-4" />
                <p className="text-muted-foreground text-center font-medium">
                  Este paciente no tiene cuenta de usuario
                </p>
                <p className="text-sm text-muted-foreground text-center mt-2">
                  El paciente necesita registrarse con su DNI para poder intercambiar mensajes
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-muted-foreground text-center">
                  No hay mensajes con este paciente
                </p>
                <p className="text-sm text-muted-foreground text-center mt-2">
                  Inicia una conversación enviando un mensaje
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${
                      msg.sender_id === user.id ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] p-3 rounded-lg ${
                        msg.sender_id === user.id
                          ? 'bg-primary text-white'
                          : 'bg-muted'
                      }`}
                    >
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {new Date(msg.created_at).toLocaleTimeString('es-PE', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                placeholder="Escribe un mensaje..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={!selectedPatient || !selectedPatient?.user_id || sendingMessage}
                data-testid="message-input"
              />
              <Button
                onClick={sendMessage}
                disabled={!selectedPatient || !selectedPatient?.user_id || !newMessage.trim() || sendingMessage}
                data-testid="send-message-button"
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            {selectedPatient && selectedPatient.user_id && (
              <p className="text-xs text-muted-foreground mt-2">
                Presiona Enter para enviar el mensaje
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
